import { useRouter } from 'next/router';
import React from 'react';
import { useTranslationContext } from '@/context/translation';
import { useTranslationWithLocale } from '@/hooks/useTranslationWithLocale';
import { buildCanonicalUrl } from '@/helpers/buildCanonicalUrl';
import { getTranslationByKey } from '@/helpers/getTranslationByKey';
import { type SeoConfig } from '@/types';
import { ENV } from '@/constants/env';
import { DEFAULT_LOCALE, VALID_LOCALES_SET } from '@/constants/locales';
import { SEOLandingLayout } from '../SEOLandingLayout';

interface Props {
	namespace: string;
	children: React.ReactNode;
	serverLocale?: string;
	serverTranslations?: Record<string, any>;
	/** Canonical URL from getServerSideProps; avoids server/client mismatch when path is rewritten. */
	serverCanonical?: string;
	/** Extra class for the footer, e.g. to clear a page's fixed mobile CTA */
	footerClassName?: string;
}

interface ClientSeoMetadataCache {
	title?: string;
	ogTitle?: string;
}

const clientSeoMetadataCache: ClientSeoMetadataCache = {};

const getExplicitLocaleFromPath = (pathname: string): string | undefined => {
	const pathWithoutQuery = pathname.split('?')[0] ?? '';
	const firstSegment = pathWithoutQuery
		.split('/')
		.filter(Boolean)[0]
		?.toLowerCase();

	return firstSegment && VALID_LOCALES_SET.has(firstSegment)
		? firstSegment
		: undefined;
};

export const SEOLandingLayoutWrapper: React.FC<Props> = ({
	namespace,
	children,
	serverLocale,
	serverTranslations,
	serverCanonical,
	footerClassName,
}) => {
	const router = useRouter();
	const { translations: contextTranslations } = useTranslationContext();
	const { pathname, asPath } = router;
	const pathForSeo = asPath?.split('?')[0] ?? pathname ?? '/';
	const localeFromPath = getExplicitLocaleFromPath(pathForSeo);

	const locale = localeFromPath ?? serverLocale ?? DEFAULT_LOCALE;

	const namespaceTranslations = serverTranslations?.[namespace];
	const contextNamespaceTranslations = contextTranslations?.[namespace];

	const { t } = useTranslationWithLocale(
		namespace,
		locale,
		namespaceTranslations,
	);

	// Canonical must match the actual URL. On client-side nav, serverCanonical can be wrong (CDN returns en).
	// Use serverCanonical only when it contains the locale from the path (e.g. /es/... for path /es/background-remover).
	const computedCanonical = buildCanonicalUrl(
		ENV.SEO_URL,
		pathForSeo,
		locale,
		DEFAULT_LOCALE,
	);
	const canonical =
		serverCanonical && serverCanonical === computedCanonical
			? serverCanonical
			: computedCanonical;

	// Fallback when translations aren't ready yet (e.g. first paint on client/stage, CDN cache)
	const rawTitle = t('metadata.title');
	const rawDescription = t('metadata.description');
	const rawOgTitle = t('metadata.ogTitle');
	const rawOgDescription = t('metadata.ogDescription');
	const fallbackDescription = 'AI-powered photo and image tools';
	const getResolvedMetadataValue = React.useCallback(
		(key: string, translatedValue: string) => {
			const serverValue = getTranslationByKey(namespaceTranslations, key);
			if (serverValue) {
				return serverValue;
			}

			const contextValue = getTranslationByKey(
				contextNamespaceTranslations,
				key,
			);
			if (contextValue) {
				return contextValue;
			}

			if (translatedValue && translatedValue !== key) {
				return translatedValue;
			}

			return undefined;
		},
		[contextNamespaceTranslations, namespaceTranslations],
	);

	const resolvedTitle = getResolvedMetadataValue('metadata.title', rawTitle);
	const description =
		rawDescription && rawDescription !== 'metadata.description'
			? rawDescription
			: fallbackDescription;
	const resolvedOgTitle = getResolvedMetadataValue(
		'metadata.ogTitle',
		rawOgTitle,
	);
	const ogDescription =
		rawOgDescription && rawOgDescription !== 'metadata.ogDescription'
			? rawOgDescription
			: fallbackDescription;
	const title =
		resolvedTitle ??
		(typeof window !== 'undefined' ? clientSeoMetadataCache.title : undefined);
	const ogTitle =
		resolvedOgTitle ??
		title ??
		(typeof window !== 'undefined'
			? clientSeoMetadataCache.ogTitle
			: undefined);

	React.useEffect(() => {
		if (typeof window === 'undefined') {
			return;
		}

		if (resolvedTitle) {
			clientSeoMetadataCache.title = resolvedTitle;
		}

		if (resolvedOgTitle) {
			clientSeoMetadataCache.ogTitle = resolvedOgTitle;
		}
	}, [resolvedOgTitle, resolvedTitle]);

	const seoConfig: SeoConfig = {
		title,
		description,
		ogTitle,
		ogDescription,
		canonical,
	};

	return (
		<SEOLandingLayout config={seoConfig} footerClassName={footerClassName}>
			{children}
		</SEOLandingLayout>
	);
};
